import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, WritableSignal, computed, signal, effect } from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import { KnowBe4Data, KnowBe4Averages } from 'src/app/api/types/mc-dashboard';
import { Subject, takeUntil } from 'rxjs';
import { DashboardDataService } from '../../../services/dashboard-data.service';

@Component({
  selector: 'app-know-be4-graphs',
  templateUrl: './know-be4-graphs.component.html',
  styleUrls: ['./know-be4-graphs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class KnowBe4GraphsComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  
  knowBe4Data: WritableSignal<KnowBe4Data[]> = signal([]);
  knowBe4Averages: WritableSignal<KnowBe4Averages> = signal({} as KnowBe4Averages);

  constructor(private dashboardDataService: DashboardDataService) {
    // Use effects to watch signals
    effect(() => {
      const data = this.dashboardDataService.knowBe4Data();
      this.knowBe4Data.set(data);
    });

    effect(() => {
      const averages = this.dashboardDataService.knowBe4Averages();
      this.knowBe4Averages.set(averages);
    });
  }

  ngOnInit(): void {
    // Initialize data loading
    this.dashboardDataService.initializeData();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // Risk Score Chart Options
  riskScoreChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    hover: {
      mode: 'x'
    },
    plugins: {
      legend: {
        position: 'bottom'
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString();
            }
            return label;
          }
        }
      },
      title: {
        display: true,
        text: 'Risk Scores'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 5,
          callback: function(value: any) {
            return value.toLocaleString();
          }
        }
      },
      x: {
        beginAtZero: true,
        ticks: {
          display: true
        }
      }
    }
  };

  // Metrics Chart Options
  metricsChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    hover: {
      mode: 'x'
    },
    plugins: {
      legend: {
        position: 'bottom'
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString();
            }
            return label;
          }
        }
      },
      title: {
        display: true,
        text: 'Security Metrics Trends'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 5,
          callback: function(value: any) {
            return value.toLocaleString();
          }
        }
      },
      x: {
        beginAtZero: true,
        ticks: {
          display: true
        }
      }
    }
  };

  // Computed chart data for risk scores
  riskScoreChartData = computed(() => {
    const data = this.knowBe4Data();
    
    // Take last 12 months of data and reverse to show chronological order
    const last12Months = data.slice(0, 12).reverse();
    
    return {
      labels: last12Months.map(item => item.graphMonth),
      datasets: [
        {
          label: 'Score',
          data: last12Months.map(item => parseFloat(item.graphRisk)),
          backgroundColor: 'rgba(151, 187, 205, 0.2)',
          borderColor: 'rgba(242, 103, 33, 0.7)',
          pointBorderColor: '#fff',
          pointBackgroundColor: 'rgba(242, 103, 33, 0.7)',
          pointHoverBackgroundColor: 'rgba(242, 103, 33, 0.7)',
          pointHoverBorderColor: 'rgba(242, 103, 33, 0.7)',
          tension: 0.4,
          fill: true
        }
      ]
    } as ChartData<'line'>;
  });

  // Computed chart data for other metrics
  metricsChartData = computed(() => {
    const data = this.knowBe4Data();

    // Take last 12 months of data and reverse to show chronological order
    const last12Months = data.slice(0, 12).reverse();

    return {
      labels: last12Months.map(item => item.graphMonth),
      datasets: [
        {
          label: '%',
          data: last12Months.map(item => parseFloat(item.graphPhish)),
          backgroundColor: 'rgba(151, 187, 205, 0.2)',
          borderColor: 'rgba(242, 103, 33, 0.7)',
          pointBorderColor: '#fff',
          pointBackgroundColor: 'rgba(242, 103, 33, 0.7)',
          pointHoverBackgroundColor: 'rgba(242, 103, 33, 0.7)',
          pointHoverBorderColor: 'rgba(242, 103, 33, 0.7)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Clicks',
          data: last12Months.map(item => item.clicks),
          backgroundColor: 'rgba(151, 187, 205, 0.2)',
          borderColor: 'rgba(151, 187, 205, 0.2)',
          pointBorderColor: '#fff',
          pointBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBorderColor: 'rgba(151, 187, 205, 0.2)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Replies',
          data: last12Months.map(item => item.replies),
          backgroundColor: 'rgba(151, 187, 205, 0.2)',
          borderColor: 'rgba(151, 187, 205, 0.2)',
          pointBorderColor: '#fff',
          pointBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBorderColor: 'rgba(151, 187, 205, 0.2)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Attachments Opened',
          data: last12Months.map(item => item.attachments),
          backgroundColor: 'rgba(151, 187, 205, 0.2)',
          borderColor: 'rgba(151, 187, 205, 0.2)',
          pointBorderColor: '#fff',
          pointBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBorderColor: 'rgba(151, 187, 205, 0.2)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Data Entered',
          data: last12Months.map(item => item.data),
          backgroundColor: 'rgba(151, 187, 205, 0.2)',
          borderColor: 'rgba(151, 187, 205, 0.2)',
          pointBorderColor: '#fff',
          pointBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBorderColor: 'rgba(151, 187, 205, 0.2)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'QR Scanned',
          data: last12Months.map(item => item.qr),
          backgroundColor: 'rgba(151, 187, 205, 0.2)',
          borderColor: 'rgba(151, 187, 205, 0.2)',
          pointBorderColor: '#fff',
          pointBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBackgroundColor: 'rgba(151, 187, 205, 0.2)',
          pointHoverBorderColor: 'rgba(151, 187, 205, 0.2)',
          tension: 0.4,
          fill: true
        }
      ]
    } as ChartData<'line'>;
  });
} 